import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";

// World topojson hosted on cdn.jsdelivr (used by react-simple-maps examples)
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Numeric UN-M49 / ISO-3166-1 to our country lookup (a few aliases to bridge naming differences)
const NAME_ALIASES = {
  "United States of America": "US",
  "United States": "US",
  "Russian Federation": "RU",
  "Russia": "RU",
  "South Korea": "KR",
  "Korea, Republic of": "KR",
  "Republic of Korea": "KR",
  "Iran (Islamic Republic of)": "IR",
  "Czech Republic": "CZ",
  "Czechia": "CZ",
  "Vietnam": "VN",
  "Viet Nam": "VN",
  "Syrian Arab Republic": "SY",
  "Bolivia (Plurinational State of)": "BO",
  "Tanzania": "TZ",
  "United Republic of Tanzania": "TZ",
  "Venezuela (Bolivarian Republic of)": "VE",
  "Türkiye": "TR",
  "Turkey": "TR",
};

export default function WorldMap({ countries = [], selectedIso2, onSelect, metric = "grid_kwh" }) {
  // Build name-keyed and iso2-keyed lookup
  const byName = {};
  const byIso2 = {};
  countries.forEach((c) => {
    if (c.name) byName[c.name.toLowerCase()] = c;
    if (c.iso2) byIso2[c.iso2] = c;
  });

  const resolve = (geo) => {
    const name = geo.properties?.name || "";
    const aliased = NAME_ALIASES[name];
    if (aliased && byIso2[aliased]) return byIso2[aliased];
    return byName[name.toLowerCase()] || null;
  };

  const max = Math.max(...countries.map((c) => Number(c[metric] || 0)));
  const min = Math.min(...countries.map((c) => Number(c[metric] || 0)));
  const colorScale = scaleLinear()
    .domain([min, (min + max) / 2, max])
    .range(["#4ADE80", "#FBBF24", "#F87171"]);

  return (
    <div className="w-full h-full theme-surface border theme-border relative overflow-hidden">
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        width={980}
        height={480}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const c = resolve(geo);
                const v = c ? Number(c[metric]) : null;
                const hasData = v !== null && !Number.isNaN(v);
                const isSelected = selectedIso2 && c?.iso2 === selectedIso2;
                const fill = hasData ? colorScale(v) : "#2A2D38";
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={isSelected ? "#D4AF37" : fill}
                    stroke={hasData ? "#0F1115" : "#3A3D48"}
                    strokeWidth={hasData ? 0.4 : 0.5}
                    data-testid={c ? `map-country-${c.iso2}` : undefined}
                    style={{
                      default: { outline: "none", opacity: hasData ? 1 : 0.55 },
                      hover: {
                        fill: hasData ? "#D4AF37" : fill,
                        outline: "none",
                        cursor: hasData ? "pointer" : "default",
                        opacity: 1,
                      },
                      pressed: { outline: "none" },
                    }}
                    onClick={() => c && onSelect && onSelect(c)}
                  >
                    {c && (
                      <title>
                        {c.name} · {metric === "grid_kwh" ? `${c.grid_kwh.toFixed(3)} kgCO₂e/kWh` : metric === "carbon_tax_usd_t" ? `$${c.carbon_tax_usd_t}/tCO₂e` : `Net-zero ${c.net_zero_year}`}
                      </title>
                    )}
                  </Geography>
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono-data theme-ink-dim uppercase tracking-widest pointer-events-none">
        <span>
          {metric === "grid_kwh"
            ? "Grid intensity (kgCO₂e/kWh)"
            : metric === "carbon_tax_usd_t"
            ? "Carbon tax (USD/tCO₂e)"
            : metric === "net_zero_year"
            ? "Net-zero target year"
            : metric}
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-eco" />Low
          <span className="inline-block w-2 h-2 rounded-full bg-warn ml-2" />Mid
          <span className="inline-block w-2 h-2 rounded-full bg-risk ml-2" />High
        </span>
      </div>
    </div>
  );
}
